/**
 * SOC checklist manual items (consent, DME, emergency plan, HHAX setup).
 * GET /patientmr/{patientId}/soc-items, PUT /patientmr/{patientId}/soc-items/{item}
 * (patientmr:phi:soc-checklist:*). Each PUT writes only that item's fields.
 */
import type { DmeEquipmentCode } from './demographics.dto';

export type SocItemName = 'consent' | 'dme' | 'emergency-plan' | 'hhax-setup' | ReadinessItemName;

/** Phase 19 F19: clinical readiness records (not checklist items) on the same route */
export type ReadinessItemName = 'advance-directive' | 'thsteps' | 'services';

export type AdvanceDirectiveStatus = 'NONE' | 'OOH_DNR' | 'MEDICAL_POA' | 'DIRECTIVE_TO_PHYSICIANS' | 'OTHER';
export const ADVANCE_DIRECTIVE_STATUSES: AdvanceDirectiveStatus[] = ['NONE', 'OOH_DNR', 'MEDICAL_POA', 'DIRECTIVE_TO_PHYSICIANS', 'OTHER'];
export const ADVANCE_DIRECTIVE_LABELS: Record<AdvanceDirectiveStatus, string> = {
  NONE: 'None',
  OOH_DNR: 'Out-of-hospital DNR',
  MEDICAL_POA: 'Medical power of attorney',
  DIRECTIVE_TO_PHYSICIANS: 'Directive to physicians',
  OTHER: 'Other',
};

export type ImmunizationStatus = 'UP_TO_DATE' | 'BEHIND' | 'UNKNOWN';
export const IMMUNIZATION_STATUS_LABELS: Record<ImmunizationStatus, string> = {
  UP_TO_DATE: 'Up to date',
  BEHIND: 'Behind',
  UNKNOWN: 'Unknown',
};

export type TriageCategory = '1' | '2' | '3' | '4';
export const TRIAGE_CATEGORIES: TriageCategory[] = ['1', '2', '3', '4'];

export type OtherServiceType = 'SHARS' | 'MDCP' | 'PCS' | 'CFC' | 'PPECC' | 'PT' | 'OT' | 'ST' | 'ECI' | 'SCHOOL' | 'OTHER';
export const OTHER_SERVICE_TYPES: OtherServiceType[] = ['SHARS', 'MDCP', 'PCS', 'CFC', 'PPECC', 'PT', 'OT', 'ST', 'ECI', 'SCHOOL', 'OTHER'];

export interface OtherService {
  type: OtherServiceType;
  provider: string;
  hoursPerWeek: number | null;
}

export interface ConsentItemInput {
  consentObtained: boolean;
  consentDate: string;
  /** Patient documents library id (type CONSENT) */
  consentDocumentId?: string;
  consentNote?: string | null;
}

export interface DmeItemInput {
  dmeConfirmed: boolean;
  dmeConfirmedDate: string;
  dmeEquipment: DmeEquipmentCode[];
  dmeNote?: string | null;
}

/** The checklist fields (on file + date) and, Phase 19 F19, the emergency readiness details; send any subset */
export interface EmergencyPlanItemInput {
  emergencyPlanOnFile?: boolean;
  emergencyPlanDate?: string;
  emergencyPlanNote?: string | null;
  emergencyTriageCategory?: TriageCategory | null;
  evacuationAssistance?: boolean;
  stearRegistered?: boolean;
  backupPower?: string | null;
  equipmentVendorName?: string | null;
  equipmentVendorPhone?: string | null;
  emergencyHospital?: string | null;
  emergencyContingencyPlan?: string | null;
}

/** 26 TAC §558.283; an OOH-DNR needs the form date, and for a minor the terminal-condition attestation */
export interface AdvanceDirectiveItemInput {
  adStatus: AdvanceDirectiveStatus;
  adFormDate?: string | null;
  adPhysician?: string | null;
  adDocumentId?: string | null;
  adTerminalDxAttested?: boolean;
  adNote?: string | null;
}

export interface ThstepsItemInput {
  thstepsLastCheckup?: string | null;
  immunizationStatus?: ImmunizationStatus;
  thstepsNote?: string | null;
}

export interface ServicesItemInput {
  otherServices?: OtherService[];
  ispDate?: string | null;
  ispDocumentId?: string | null;
  serviceCoordinatorName?: string | null;
  serviceCoordinatorPhone?: string | null;
}

export interface HhaxSetupItemInput {
  hhaxSetupComplete: boolean;
  hhaxSetupDate: string;
  hhaxSetupNote?: string | null;
}

export interface SocItemInputMap {
  consent: ConsentItemInput;
  dme: DmeItemInput;
  'emergency-plan': EmergencyPlanItemInput;
  'hhax-setup': HhaxSetupItemInput;
  'advance-directive': AdvanceDirectiveItemInput;
  thsteps: ThstepsItemInput;
  services: ServicesItemInput;
}

/** Shared (patient-level) checklist computed by the server; hhaxSetupComplete only when it applies */
export interface SocSharedChecklist {
  demographicsComplete: boolean;
  insuranceVerified: boolean;
  physicianOrderComplete: boolean;
  physicianOrderSigned?: boolean;
  consentObtained: boolean;
  dmeConfirmed: boolean;
  emergencyPlanOnFile: boolean;
  hhaxSetupComplete?: boolean;
}

export interface SocItemsResponse {
  patientId: string;
  consentObtained: boolean | null;
  consentDate: string | null;
  consentDocumentId: string | Record<string, unknown> | null;
  consentNote: string | null;
  dmeConfirmed: boolean | null;
  dmeConfirmedDate: string | null;
  dmeEquipment: DmeEquipmentCode[] | null;
  dmeNote: string | null;
  emergencyPlanOnFile: boolean | null;
  emergencyPlanDate: string | null;
  emergencyPlanNote: string | null;
  hhaxSetupComplete: boolean | null;
  hhaxSetupDate: string | null;
  hhaxSetupNote: string | null;
  hhaxSetupApplies: boolean;
  checklist: SocSharedChecklist;
  // Phase 19 F19 readiness records (null when unset)
  emergencyTriageCategory?: TriageCategory | null;
  evacuationAssistance?: boolean | null;
  stearRegistered?: boolean | null;
  backupPower?: string | null;
  equipmentVendorName?: string | null;
  equipmentVendorPhone?: string | null;
  emergencyHospital?: string | null;
  emergencyContingencyPlan?: string | null;
  adStatus?: AdvanceDirectiveStatus | null;
  adFormDate?: string | null;
  adPhysician?: string | null;
  adDocumentId?: string | null;
  adTerminalDxAttested?: boolean | null;
  adNote?: string | null;
  thstepsLastCheckup?: string | null;
  immunizationStatus?: ImmunizationStatus | null;
  thstepsNote?: string | null;
  /** Server-computed from the date of birth and the last checkup (null past age 21 or without a DOB) */
  thstepsNextDue?: string | null;
  otherServices?: OtherService[] | null;
  ispDate?: string | null;
  ispDocumentId?: string | null;
  serviceCoordinatorName?: string | null;
  serviceCoordinatorPhone?: string | null;
}
